import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { auth, db } from '../config/firebase';
import { AdminUser, WhiteLabelConfig } from '../types';

interface AuthContextType {
  user: User | null;
  adminUser: AdminUser | null;
  whiteLabelConfig: WhiteLabelConfig | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [adminUser, setAdminUser] = useState<AdminUser | null>(null);
  const [whiteLabelConfig, setWhiteLabelConfig] = useState<WhiteLabelConfig | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchAdminData = async (uid: string) => {
    try {
      const adminDoc = await getDoc(doc(db, 'adminUsers', uid));
      if (adminDoc.exists()) {
        const data = adminDoc.data() as AdminUser;
        setAdminUser(data);
        
        // Set white label configuration
        setWhiteLabelConfig({
          institutionName: data.institutionName,
          institutionLogo: data.institutionLogo,
          brandColor: data.brandColor,
          primaryColor: data.brandColor,
          secondaryColor: '#1890ff',
        });

        // Update last login
        await updateDoc(doc(db, 'adminUsers', uid), {
          lastLogin: new Date(),
        });
      }
    } catch (error) {
      console.error('Error fetching admin data:', error);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      if (user) {
        await fetchAdminData(user.uid);
      } else {
        setAdminUser(null);
        setWhiteLabelConfig(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    await fetchAdminData(userCredential.user.uid);
  };

  const logout = async () => {
    await signOut(auth);
    setAdminUser(null);
    setWhiteLabelConfig(null);
  };

  const value = {
    user,
    adminUser,
    whiteLabelConfig,
    loading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
